import {mapActions, mapGetters, mapState} from 'vuex';
import moment from 'moment';
import {IdentityTypes, TotalPeriods} from './constants';
import debounce from 'debounce';
import {groupBy} from './utilities';
import roomMixin from './roomMixin';

const defaultForm = {
    contact: '',
    email: '',
    realUserId: '',
    dates: [],
    roomId: '',
    reason: ''
};
const defaultRules = {
    email: [
        {required: true, message: '請輸入電子郵件信箱', trigger: 'blur'},
        {
            pattern: /^\w+((-\w+)|(\.\w+))*@[A-Za-z0-9]+((\.|-)[A-Za-z0-9]+)*\.[A-Za-z]+$/
            , message: '郵件格式錯誤', trigger: 'blur'
        },
    ],
    dates: [
        {required: true, message: '請選擇借用時段'}
    ],
    contact: [
        {required: true, message: '請輸入連絡電話', trigger: 'blur'},
        {
            pattern: /^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s#./0-9]*$/g
            , message: '請輸入合法的電話格式', trigger: 'blur'
        },
    ],
    realUserId: [
        {required: true, message: '請輸入使用者', trigger: 'blur'},
    ],
    roomId: [
        {required: true, message: '請選擇教室', trigger: 'blur'},
    ],
    reason: [
        {required: true, message: '請輸入事由', trigger: 'blur'},
    ],
};

export default {
    data: () => ({
        model: {...defaultForm},
        rules: {...defaultRules},
        periodOpt: [...new Array(TotalPeriods).keys()].map(k => ({value: k + 1, key: k + 1, label: `第${k + 1}節`})),
        reasonType: 'course',
        show: false,
    }),
    mixins:[roomMixin],
    computed: {
        ...mapState(['loading']),
        ...mapState('teacher', ['teachers']),
        ...mapState('course', ['courses', 'coursesOfTeacher']),
        ...mapState('setting', {
            dateEnd: state => state.sys.dateEnd,
            dateBegin: state => state.sys.dateBegin,
        }),
        ...mapState('auth', {
            name: state => state.name,
            userId: state => state.userId,
            identityType: state => state.identityType
        }),
        ...mapState('setting', ['periodsDefs']),
        ...mapGetters('setting', ['schoolYear', 'semester']),
        formattedDates() {
            if (this.model.dates) {
                return this.model.dates.map(d => moment(d).format('YYYY-MM-DD'))
            }
            return [];
        },
        isEmployee() {
            return this.identityType === IdentityTypes.Employee;
        },
        isStudent() {
            return this.identityType === IdentityTypes.Student;
        },
        getTeacherSearchFunc() {
            const self = this;
            return debounce(
                async (value) => {
                    await self.getTeachersWithKey(value);
                },
                200
            )
        },
        getCourseSearchFunc() {
            const self = this;
            return debounce(
                async (value) => {
                    await self.getCoursesWithKey(value);
                },
                300
            )
        },
        optionCourses() {
            const all = this.courses.concat(this.coursesOfTeacher);
            const grpById = groupBy(all, c => c.courseId);
            return Object.keys(grpById)
                .reduce((c, g) => ([...c, grpById[g][0]]), []) || [];
        },
        selectedRoom() {
            return this.rooms.find(r => r.id === this.model.roomId);
        },
        bookingDateBegin() {
            const today = moment().startOf('day');
            const configuredBegin = moment(this.dateBegin);
            return configuredBegin.isValid() && configuredBegin.isAfter(today, 'd')
                ? configuredBegin.startOf('day').toDate()
                : today.toDate();
        },
        queryTitle(){
            if(this.selectedRoom){
                return `${this.selectedRoom.name}課表及借用情況`
            }
            return '';
        }
    },
    methods:{
        ...mapActions('teacher', {
            getTeachersWithKey: 'getTeachersWithKeyAction'
        }),
        ...mapActions('course', {
            getCourses: 'getCoursesOfAction',
            getCoursesWithKey: 'getCoursesWithKeyAction',
        }),
        ...mapActions('booking', {
            isCollided: 'isCollidedAction',
        }),
        ...mapActions('setting', {
            getWeekRanges: 'getWeekRangesAction'
        }),
        onShowInfo() {
            this.show = true;
        },

        validatePeriod(rule, value, callback) {
            if (!value || value.length < 2 || value.some(v => !v)) {
                callback(new Error('請選擇節次起訖'));
            }
            callback();
        },

        rebuildRules(dates) {
            const self = this;
            const rules = dates.reduce((r, d) => ({
                ...r,
                [moment(d).format('YYYY-MM-DD')]:
                    [{validator: self.validatePeriod}]
            }), {});
            this.rules = {...defaultRules, ...rules}
        },

        onDateSelect(dates) {
            const selectedDates = (dates || []).filter(date => this.isBookingDate(date));
            if (selectedDates.length !== (dates || []).length) {
                this.model.dates = selectedDates;
            }
            this.rebuildRules(selectedDates);
            const periodProps = Object.keys(this.model)
                .filter(d => moment(d, 'YYYY-MM-DD').isValid());

            const newKeys = new Set(selectedDates.map(d => moment(d).format('YYYY-MM-DD')));
            const currentKeys = new Set(periodProps);

            const existed = newKeys.intersection(currentKeys);
            const insert = newKeys.difference(currentKeys);
            const deleted = currentKeys.difference(existed.union(insert));
            const periods = {
                ...[...existed].reduce((o, k) => ({
                    [k]: this.model[k],
                    ...o
                }), {}),
                ...[...insert].reduce((o, k) => ({
                    [k]: ['', ''],
                    ...o
                }), {})
            };
            this.model = {...this.model, ...periods};
            deleted.forEach(d => this.$delete(this.model, d))
        },
        onCancelDate(date) {
            this.model.dates = this.model.dates.filter(d => moment(d).format('YYYY-MM-DD') !== date);
            this.rebuildRules(this.model.dates);
            this.$delete(this.model, moment(date).format('YYYY-MM-DD'));
        },

        onCancelAll() {
            this.model.dates = [];
            this.rebuildRules(this.model.dates);
            Object.keys(this.model)
                .filter(d => moment(d, 'YYYY-MM-DD').isValid())
                .forEach(d => this.$delete(this.model, d));
        },

        onChangeRoom() {
            this.onCancelAll();
        },

        reset() {
            this.$refs.form.clearValidate();
            this.rules = {...defaultRules};
            this.model = {...defaultForm};
            this.reasonType = 'course';
            this.$refs.form.resetFields();
        },

        async checkCollided(begin, end, date){
            const {courseCollided, booked} = await this.isCollided({
                date,
                roomId: this.selectedRoom.ncutRoomId,
                begin,
                end,
                semester: this.semester,
                schoolYear: this.schoolYear
            });

            if (courseCollided) {
                this.$message({
                    type: 'warning',
                    message: '這個時段已排課(或調課)!，請重新選擇'
                })
            }

            if (booked) {
                this.$message({
                    type: 'warning',
                    message: '這個時段已被借用!，請重新選擇'
                })
            }

            if (courseCollided || booked) {
                this.model[date] = ['', ''];
            }
        },

        async onBeginChange(begin, date){
            if (this.model[date]) {
                const [end] = this.model[date];
                if(end){
                   await this.checkCollided(begin, end, date);
                }
            }
        },

        async onEndChange(end, date) {
            if (this.model[date]) {
                const [begin] = this.model[date];
                await this.checkCollided(begin, end, date);
            }
        },

        async onSelectRealUser(value) {
            await this.getCourses(value);
        },

        isBookingDate(date) {
            return moment(date).isBetween(this.dateBegin, this.dateEnd, 'd', '[]')
                && !moment(date).isBefore(moment(), 'd');
        },

        onCellClick(data) {
            const {isValid, isPast, date, period} = data;
            if (isValid && !isPast && this.isBookingDate(date)) {
                if (this.model.hasOwnProperty(date)) {
                    this.$message({
                        type: 'warning',
                        message: '此日期已選擇過了，請重新選擇'
                    });
                } else {
                    this.model = {...this.model, [date]: [period, period]};
                    this.model.dates = [...this.model.dates, new Date(date)];
                    this.show = false;
                }
            }

        },

        getPeriodsOptsWithTime(day){
            const isWeekend = moment(day).isoWeekday() === 6 || moment(day).isoWeekday() === 7;
            const which = (isWeekend ? this.periodsDefs.collegePeriods : this.periodsDefs.dayPeriods);
            return this.periodOpt.map(p => ({
                ...p,
                label: `${p.label}(${which.find(w => w.period === p.key).text})`
            }));
        }

    },
    watch: {
        rooms(current) {
            this.model.roomId = (current || [{id: ''}])[0].id;
        },
        reasonType() {
            this.model.reason = '';
        }
    },
    mounted(){
        this.model = {...defaultForm};
    },
}
